import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Waveform, MagicWand, TextT, SquaresFour, Play, Pause,
  ArrowRight, X, Sparkle, Anchor, SunHorizon, Waves,
} from "@phosphor-icons/react";
import { useLang } from "@/i18n/LanguageContext";

const FEATURES = [
  {
    icon: TextT,
    title: "Describe the sound in words",
    text: "Just type an idea in the prompt field — “thunder rolling over the mountains” — and get a ready-made sound effect.",
    hint: "Prompt field · top left",
  },
  {
    icon: MagicWand,
    title: "Prompt Enhancer (AI)",
    text: "The magic wand turns a short phrase into a rich, cinematic description — dramatically boosting sound quality.",
    hint: "Wand button · below the input",
  },
  {
    icon: SquaresFour,
    title: "Preset Library",
    text: "32 ready-made professional prompts across 8 categories: from theater applause to sci-fi starships.",
    hint: "Categories · Nature, Horror, Cinema…",
  },
];

const MASTERPIECES = [
  {
    slug: "calm-atlantic-wave",
    icon: Waves,
    title: "Calm Atlantic Wave",
    desc: "Clean ASMR wave rolling onto sand, with long pauses of silence",
    tint: "from-cyan-500/20",
    accent: "text-cyan-300",
  },
  {
    slug: "the-ghost-ship",
    icon: Anchor,
    title: "The Ghost Ship",
    desc: "Creaking heavy ropes, a dark cinematic sea storm",
    tint: "from-red-500/20",
    accent: "text-red-300",
  },
  {
    slug: "summer-morning-cuckoo",
    icon: SunHorizon,
    title: "Summer Morning Cuckoo",
    desc: "Bright forest with a fading cuckoo echo",
    tint: "from-amber-500/20",
    accent: "text-amber-300",
  },
];

const EqBars = () => (
  <span className="inline-flex items-end gap-[3px] h-4" aria-hidden>
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className="w-[3px] rounded-full bg-current"
        style={{
          height: `${6 + (i % 3) * 4}px`,
          animation: `aurafx-eq 0.6s ease-in-out ${i * 0.12}s infinite alternate`,
        }}
      />
    ))}
    <style>{`@keyframes aurafx-eq { from { transform: scaleY(0.35); } to { transform: scaleY(1); } }`}</style>
  </span>
);

export default function OnboardingShowcase({ onClose }) {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const { t } = useLang();

  useEffect(() => {
    return () => audioRef.current?.pause();
  }, []);

  const dismiss = () => {
    localStorage.setItem("has_seen_promo", "1");
    audioRef.current?.pause();
    onClose();
  };

  const togglePlay = (slug) => {
    if (playing === slug) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(`/showcase/${slug}.mp3`);
    audio.onended = () => setPlaying(null);
    audio.play().catch(() => setPlaying(null));
    audioRef.current = audio;
    setPlaying(slug);
  };

  return (
    <div
      data-testid="onboarding-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-xl" onClick={dismiss} />
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-3xl max-h-[calc(100dvh-2rem)] rounded-3xl p-[1px] bg-gradient-to-br from-[color:var(--accent)]/60 via-white/10 to-transparent shadow-[0_0_80px_-20px_rgba(229,9,20,0.45)] flex"
      >
        <div className="rounded-3xl bg-[#0b0b0f]/95 backdrop-blur-2xl overflow-hidden flex flex-col w-full min-h-0">
          {/* Header */}
          <div className="px-5 sm:px-8 pt-5 sm:pt-7 pb-4 sm:pb-5 flex items-start justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[color:var(--accent)] grid place-items-center shadow-[0_0_24px_-4px_rgba(229,9,20,0.8)]">
                <Waveform size={22} weight="fill" color="white" />
              </div>
              <div>
                <p className="text-[10px] font-mono-num uppercase tracking-[0.25em] text-white/40">
                  {t("welcomeTo")}
                </p>
                <h2 className="font-display font-bold text-xl tracking-tight">AuraFX Studio</h2>
              </div>
            </div>
            <button
              data-testid="onboarding-close-btn"
              onClick={dismiss}
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
          <AnimatePresence mode="wait">
            {step === 0 ? (
              <motion.div
                key="tour"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
                className="px-5 sm:px-8 pb-6 sm:pb-8"
              >
                <h3 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight leading-tight">
                  Sound for theater, film and animation —{" "}
                  <span className="text-[color:var(--accent)]">from a single phrase</span>
                </h3>
                <p className="mt-2 text-sm text-white/50 max-w-lg">
                  Three things worth knowing before your first sound:
                </p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {FEATURES.map((f, i) => (
                    <motion.div
                      key={f.title}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 + i * 0.12 }}
                      data-testid="onboarding-feature-card"
                      className="group rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[color:var(--accent)]/40 hover:bg-white/[0.05] p-5 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-lg bg-[color:var(--accent)]/15 border border-[color:var(--accent)]/30 grid place-items-center text-[color:var(--accent)]">
                        <f.icon size={18} weight="fill" />
                      </div>
                      <h4 className="mt-3 font-medium text-sm">{f.title}</h4>
                      <p className="mt-1.5 text-xs leading-relaxed text-white/50">{f.text}</p>
                      <p className="mt-3 text-[10px] font-mono-num uppercase tracking-widest text-[color:var(--accent)]/70 inline-flex items-center gap-1">
                        <ArrowRight size={10} /> {f.hint}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="showcase"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
                className="px-5 sm:px-8 pb-6 sm:pb-8"
              >
                <h3 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight leading-tight">
                  Built with our AI <Sparkle size={22} weight="fill" className="inline text-[color:var(--accent)] -mt-1" />
                </h3>
                <p className="mt-2 text-sm text-white/50">
                  Listen to what the platform can do — before your first prompt:
                </p>
                <div className="mt-6 space-y-3">
                  {MASTERPIECES.map((m, i) => (
                    <motion.div
                      key={m.slug}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.12 + i * 0.1 }}
                      className={`rounded-2xl border border-white/10 bg-gradient-to-r ${m.tint} to-transparent p-4 flex items-center gap-4`}
                    >
                      <button
                        data-testid={`showcase-play-${m.slug}`}
                        onClick={() => togglePlay(m.slug)}
                        className={`shrink-0 w-12 h-12 rounded-full grid place-items-center transition-all ${
                          playing === m.slug
                            ? "bg-[color:var(--accent)] shadow-[0_0_28px_-4px_rgba(229,9,20,0.9)] scale-105"
                            : "bg-white/10 hover:bg-white/20 border border-white/15"
                        }`}
                      >
                        {playing === m.slug ? (
                          <Pause size={20} weight="fill" color="white" />
                        ) : (
                          <Play size={20} weight="fill" color="white" />
                        )}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <m.icon size={15} weight="fill" className={m.accent} />
                          <h4 className="font-medium text-sm truncate">{m.title}</h4>
                          {playing === m.slug && (
                            <span className={m.accent}><EqBars /></span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-white/50 truncate">{m.desc}</p>
                      </div>
                      <span className="hidden sm:block text-[10px] font-mono-num uppercase tracking-widest text-white/30">
                        SFXAura · SFX
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-5 sm:px-8 py-4 sm:py-5 border-t border-white/5 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              {[0, 1].map((i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    step === i ? "w-6 bg-[color:var(--accent)]" : "w-1.5 bg-white/20"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="onboarding-skip-btn"
                onClick={dismiss}
                className="text-sm text-white/40 hover:text-white/70 transition-colors"
              >
                {t("onbSkip")}
              </button>
              {step === 0 ? (
                <button
                  data-testid="onboarding-next-btn"
                  onClick={() => setStep(1)}
                  className="btn-primary px-5 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2"
                >
                  {t("onbNext")} <ArrowRight size={15} weight="bold" />
                </button>
              ) : (
                <button
                  data-testid="onboarding-start-btn"
                  onClick={dismiss}
                  className="btn-primary px-5 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2"
                >
                  <Sparkle size={15} weight="fill" /> {t("onbStart")}
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
